import React, { useEffect, useState } from "react";
import api from "../../../utils/axios";
import moment from "moment";
import "moment/locale/th";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import Pagination from "../../components/Pagination";
import DatePicker from "../../components/DatePicker";

export default function SaleAppointReport() {
  const [data, setData] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const breadcrumbs = [
    { label: "ขาย", link: "/sale" },
    { label: "รายงานนัดหมายลูกค้า" },
  ];

  const fetchData = async (date = "") => {
    setLoading(true);
    try {
      const url = date
        ? `/get-sale-appoint-summary?date=${date}`
        : `/get-sale-appoint-summary`;
      const res = await api.get(url);
      if (res.data?.status === "OK") {
        setData(res.data.result || []);
      } else {
        Swal.fire("เกิดข้อผิดพลาด", res.data?.message || "โหลดข้อมูลไม่สำเร็จ", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("ผิดพลาด", err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    const savedDate = localStorage.getItem("appointReportDate");
    const savedSearch = localStorage.getItem("appointReportSearch");

    const dateToUse = savedDate || today;
    setSelectedDate(dateToUse);
    setSearchText(savedSearch || "");
    fetchData(dateToUse);
  }, []);

  const formatThaiDate = (date) => {
    if (!date) return "-";
    const m = moment(date);
    return m.format(`DD/MMM/${m.year() + 543}`);
  };

  const formatThaiDateTime = (date) => {
    if (!date) return "–";
    const m = moment(date);
    return m.format(`DD/MMM/${m.year() + 543}, HH:mm`);
  };

  const filteredData = data.filter((d) =>
    d.Emp_Name?.toLowerCase().includes(searchText.toLowerCase())
  );

  const totalItems = filteredData.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage);

  return (
    <>
      <Sidebar />
      <Topbar breadcrumbs={breadcrumbs} />

      <div
        className="p-6"
        style={{
          marginLeft: "var(--sidebar-width)",
          marginTop: "56px",
          backgroundColor: "#f8f9fa",
          minHeight: "calc(100vh - 56px)",
        }}
      >
        <div
          className="card table-card border-0 rounded-0 shadow-sm bg-white"
          style={{
            display: "flex",
            flexDirection: "column",
            padding: "8px 12px",
          }}
        >
          <style>{`
            * { font-size: 14px !important; }
            table.table {
              border-collapse: collapse;
              width: 100%;
              min-width: 1000px;
            }
            th, td {
              text-align: center !important;
              vertical-align: middle !important;
              border: 1px solid #ccc !important;
              padding: 6px 10px !important;
              white-space: nowrap;
            }
            thead th {
              background-color: #E13F57 !important;
              color: white !important;
              font-weight: 600;
            }
            tbody tr:nth-child(odd) { background: #fafafa; }
            tbody tr:nth-child(even) { background: #fff; }
            tbody tr:hover { background: #f5f7ff !important; }
          `}</style>

          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2 px-2 pt-2">
            <h3 className="fw-bold text-gray-700 fs-5">รายงานนัดหมายลูกค้า</h3>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              {/* 🔍 ค้นหา */}
              <input
                type="text"
                placeholder="ค้นหาชื่อลูกค้า..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="form-control"
                style={{ width: "200px", height: "38px" }}
              />

              {/* 📅 วันที่ */}
              <div style={{ width: "240px" }}>
                <DatePicker
                  name="selectedDate"
                  value={selectedDate}
                  onChange={(newDate) => {
                    setSelectedDate(newDate);
                    fetchData(newDate);
                  }}
                  disabled={loading}
                  className="form-control"
                  style={{ height: "38px" }}
                />
              </div>

              {/* 🔘 ปุ่มรีเซ็ต */}
              {selectedDate && (
                <button
                  onClick={() => {
                    const today = new Date().toISOString().split("T")[0];
                    setSelectedDate(today);
                    fetchData(today);
                  }}
                  className="btn btn-outline-danger"
                  style={{
                    height: "38px",
                    display: "flex",
                    alignItems: "center",
                    padding: "0 16px",
                  }}
                >
                  รีเซ็ตเป็นวันนี้
                </button>
              )}
            </div>

          </div>

          {/* Table */}
          <div
            className="table-wrapper-scroll flex-1"
            style={{
              overflowY: "auto",
              overflowX: "auto",
              flexGrow: 1,
              padding: "4px 6px",
            }}
          >
            <table className="table table-bordered table-hover mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th className="text-start">พนักงานขาย</th>
                  <th>วันที่</th>
                  <th>จำนวนการนัด</th>
                  <th>นัดหมายล่าสุด</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="text-center py-4 text-muted">
                      กำลังโหลดข้อมูล...
                    </td>
                  </tr>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((row, idx) => (
                    <tr key={row.Emp_Id}>
                      <td>{startIndex + idx + 1}</td>
                      <td className="text-start text-primary fw-semibold">
                        <Link
                          to={`/sale-appoint-detail/${row.Emp_Id}?date=${selectedDate}`}
                          onClick={() => {
                            localStorage.setItem("appointReportDate", selectedDate);
                            localStorage.setItem("appointReportSearch", searchText);
                          }}
                          className="text-decoration-underline"
                        >
                          {row.Emp_Name}
                        </Link>
                      </td>
                      <td>{formatThaiDate(selectedDate)}</td>
                      <td>{row.TotalAppoints}</td>
                      <td>{formatThaiDateTime(row.LastAppointDate)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-4 text-muted">
                      ไม่พบข้อมูล
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer Pagination */}
          <div className="p-2 border-top bg-white">
            <Pagination
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </div>
        </div>
      </div>
    </>
  );
}
